import React from "react";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import MentionInput from "../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "../common/Common.types.ts";
import { EntityTemplateField, isSystemStandardField, resolveSupplierPermissions, SystemStandardField, TemplateEntityType } from "./EntityTemplate.types.ts";
import { OperationCriticalityLevelEnum, SystemModel, SystemStatus } from "../system/systemEdit/SystemEdit.types.ts";
import { ValidationError } from "../common/validationError.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { exhaustiveGuard } from "../common/utilities.ts";
import CustomTextField from "./fields/customFields/CustomTextField.tsx";
import CustomRichTextField from "./fields/customFields/CustomRichTextField.tsx";
import CustomSingleSelectField from "./fields/customFields/CustomSingleSelectField.tsx";
import CustomCountryField from "./fields/customFields/CustomCountryField.tsx";
import CustomMultiSelectField from "./fields/customFields/CustomMultiSelectField.tsx";
import CustomDateField from "./fields/customFields/CustomDateField.tsx";
import CustomNumberField from "./fields/customFields/CustomNumberField.tsx";
import CustomAmountField from "./fields/customFields/CustomAmountField.tsx";
import CustomCheckbox from "./fields/customFields/CustomCheckbox.tsx";
import CustomLegalEntityField from "./fields/customFields/CustomLegalEntityField.tsx";
import CustomUserField from "./fields/customFields/CustomUserField.tsx";
import { useTranslation } from "../localization/useTranslation.ts";
import { isNullOrWhitespace } from "../common/stringOperations.ts";
import AccessAwareResponsibleField from "./fields/AccessAwareResponsibleField.tsx";
import BusinessAreaField from "./fields/BusinessAreaField.tsx";
import SystemTypeField from "./fields/systemFields/SystemTypeField.tsx";
import SystemStorageTypeField from "./fields/systemFields/SystemStorageTypeField.tsx";
import SystemEntityTypeField from "./fields/systemFields/SystemEntityTypeField.tsx";
import SystemDataTypesField from "./fields/systemFields/SystemDataTypesField.tsx";
import SystemDataStorageLocationsField from "./fields/systemFields/SystemDataStorageLocationsField.tsx";
import SystemSupportAccessLocationsField from "./fields/systemFields/SystemSupportAccessLocationsField.tsx";
import DeletionPeriodField from "./fields/DeletionPeriodField.tsx";
import SystemSecurityMeasuresField from "./fields/systemFields/SystemSecurityMeasuresField.tsx";
import SystemCertificationsField from "./fields/systemFields/SystemCertificationsField.tsx";
import SystemAIField from "./fields/systemFields/SystemAIField.tsx";
import SystemIsCriticalForOperationField from "./fields/systemFields/SystemIsCriticalForOperationField.tsx";
import SystemAssociatedAssetsField from "./fields/systemFields/systemAssociatedAssets/SystemAssociatedAssetsField.tsx";
import SystemPoliciesField from "./fields/systemFields/SystemPoliciesField.tsx";
import SystemDataSubjectsField from "./fields/systemFields/systemDataSubjects/SystemDataSubjectsField.tsx";
import SystemDataCategoriesField from "./fields/systemFields/systemDataCategories/SystemDataCategoriesField.tsx";
import SystemSuppliersField from "./fields/systemFields/systemSupplier/SystemSuppliersField.tsx";
import { TagRelationType } from "../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import TagsMultiSelectStandAlone from "../common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import AdditionalUserAccessField from "./fields/AdditionalUserAccessField.tsx";
import SystemExcludedUsersField from "./fields/systemFields/SystemExcludedUsersField.tsx";
import VisibleToAllUsersField from "./fields/VisibleToAllUsersField.tsx";
import BusinessAreaAccessField from "./fields/BusinessAreaAccessField.tsx";
import GroupCompaniesAccessField from "./fields/GroupCompaniesAccessField.tsx";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../useUrlProvider.ts";
import SystemStatusField from "./fields/systemFields/SystemStatusField.tsx";
import CustomCalculatedField from "./fields/customFields/CustomCalculatedField.tsx";
import CustomDatePeriodField from "./fields/customFields/CustomDatePeriodField.tsx";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";

export interface SystemStandardFieldProps {
    field: EntityTemplateField;
    isLoading: boolean;
    system?: SystemModel;
    systemId: string;
    onChange: (changeFunc: (system: SystemModel) => void) => Promise<void>;
    onRefetchSystem: () => void;
    validationErrors: Array<ValidationError>;
    isPreview: boolean;
    disableAccessWarning?: boolean;
}

export interface SystemCustomFieldProps {
    systemId: string;
    system?: SystemModel;
    field: EntityTemplateField;
    onChange: (changeFunc: (system: SystemModel) => void) => void;
    isPreview: boolean;
    isLoading: boolean;
    validationErrors: Array<ValidationError>;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export interface SystemFieldRendererProps {
    field: EntityTemplateField;
    system?: SystemModel;
    isLoading: boolean;
    systemId?: string;
    onChange: (changeFunc: (system: SystemModel) => void) => Promise<void>;
    onRefetchSystem: () => void;
    validationErrors?: Array<ValidationError>;
    onCustomFieldChange: (changeFunc: (system: SystemModel) => void) => void;
    isPreview: boolean;

    // Template editor preview props
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export const SystemStandardFieldRenderer: React.FC<SystemStandardFieldProps> = ({
    field,
    system,
    systemId,
    onChange,
    validationErrors,
    isLoading,
    isPreview,
    disableAccessWarning,
}) => {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    if (!field.standardFieldIdentifier || !isSystemStandardField(field.standardFieldIdentifier)) {
        return null;
    }

    const isDisabled = field.disabled || isPreview;

    switch (field.standardFieldIdentifier) {
        case SystemStandardField.SystemName:
            return (
                <DotLegalTextField
                    label={translateString("asset")}
                    value={system?.name ?? ""}
                    debounce
                    toolTipText={translateString("systemNameTooltip")}
                    onChange={(value) =>
                        onChange((s) => {
                            s.name = value;
                        })
                    }
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemName].toString())?.error}
                    disabled={isDisabled}
                />
            );
        case SystemStandardField.SystemDescription:
            return (
                <MentionInput
                    multiline
                    rows={12}
                    label={translateString("description")}
                    value={system?.description ?? ""}
                    debounce
                    toolTipText={translateString("systemDescriptionTooltip")}
                    onChange={(value) =>
                        onChange((s) => {
                            s.description = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDescription].toString())?.error}
                    {...(isNullOrWhitespace(systemId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: systemId, entityFromType: EntityType.System } })}
                />
            );
        case SystemStandardField.SystemResponsible:
            return (
                <AccessAwareResponsibleField
                    entity={system}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getSystemsUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("systemResponsibleTooltip")}
                    warningText={isPreview ? undefined : translateString("systemResponsibleWarning")}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemResponsible].toString())?.error}
                />
            );
        case SystemStandardField.DistributionArea:
            return (
                <SystemTypeField
                    distributionArea={system?.distributionArea}
                    onChange={(distributionArea) =>
                        onChange((s) => {
                            s.distributionArea = distributionArea;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.DistributionArea].toString())?.error}
                />
            );
        case SystemStandardField.SystemEntityType:
            return (
                <SystemEntityTypeField
                    entityType={system?.entityType}
                    onChange={(type) =>
                        onChange((s) => {
                            s.entityType = type ?? undefined;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemEntityType].toString())?.error}
                />
            );
        case SystemStandardField.SystemStorageType:
            return (
                <SystemStorageTypeField
                    storageType={system?.storageType}
                    onChange={(type) =>
                        onChange((s) => {
                            s.storageType = type ?? undefined;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemStorageType].toString())?.error}
                />
            );
        case SystemStandardField.SystemDeletionPeriod:
            return (
                <DeletionPeriodField
                    deletionPeriodId={system?.deletionPeriodId}
                    deletionPeriodReason={system?.deletionPeriodReason}
                    onDeletionPeriodChange={(value) =>
                        onChange((s) => {
                            s.deletionPeriodId = value;
                        })
                    }
                    onDeletionPeriodReasonChange={(value) =>
                        onChange((s) => {
                            s.deletionPeriodReason = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDeletionPeriod].toString())?.error}
                />
            );
        case SystemStandardField.SystemNetwork:
            return (
                <DotLegalTextField
                    label={translateString("network")}
                    value={system?.network ?? ""}
                    debounce
                    toolTipText={translateString("systemNetworkTooltip")}
                    onChange={(value) =>
                        onChange((s) => {
                            s.network = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemNetwork].toString())?.error}
                />
            );
        case SystemStandardField.SystemIsCriticalForOperation:
            return (
                <SystemIsCriticalForOperationField
                    isCriticalForOperation={system?.isCriticalForOperation ?? false}
                    criticalityLevel={system?.operationCriticalityLevel}
                    onIsCriticalForOperationChange={(value) =>
                        onChange((s) => {
                            s.isCriticalForOperation = value;
                            if (value && !s.operationCriticalityLevel) {
                                s.operationCriticalityLevel = OperationCriticalityLevelEnum.Moderate;
                            }
                            if (!value) {
                                s.operationCriticalityLevel = undefined;
                            }
                        })
                    }
                    onCriticalityLevelChange={(value) =>
                        onChange((s) => {
                            s.operationCriticalityLevel = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={
                        validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemIsCriticalForOperation].toString())?.error
                    }
                />
            );
        case SystemStandardField.SystemUsesAI:
            return (
                <SystemAIField
                    usesAI={system?.usesAI ?? false}
                    companyUsesAIService={system?.companyUsesAIService ?? false}
                    aiUsageDescription={system?.aiUsageDescription}
                    isUseOfAIConsideredHighRisk={system?.isUseOfAIConsideredHighRisk ?? false}
                    isDataUsedForTraningOfAIModel={system?.isDataUsedForTraningOfAIModel ?? false}
                    systemId={systemId}
                    onUsesAIChange={(value) =>
                        onChange((s) => {
                            s.usesAI = value;
                        })
                    }
                    onCompanyUsesAIServiceChange={(value) =>
                        onChange((s) => {
                            s.companyUsesAIService = value;
                        })
                    }
                    onAiUsageDescriptionChange={(value) =>
                        onChange((s) => {
                            s.aiUsageDescription = value;
                        })
                    }
                    onIsUseOfAIConsideredHighRiskChange={(value) =>
                        onChange((s) => {
                            s.isUseOfAIConsideredHighRisk = value;
                        })
                    }
                    onIsDataUsedForTraningOfAIModelChange={(value) =>
                        onChange((s) => {
                            s.isDataUsedForTraningOfAIModel = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemUsesAI].toString())?.error}
                />
            );
        case SystemStandardField.SystemBusinessAreas:
            return (
                <BusinessAreaField
                    businessAreas={system?.businessAreas ?? []}
                    isLoading={isLoading}
                    onChange={(value) =>
                        onChange((s) => {
                            s.businessAreas = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("systemBusinessAreasTooltip")}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemBusinessAreas].toString())?.error}
                />
            );
        case SystemStandardField.SystemSuppliers:
            return (
                <SystemSuppliersField
                    system={system}
                    validationErrors={validationErrors}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    supplierPermissions={isPreview ? resolveSupplierPermissions() : resolveSupplierPermissions(field.subPermissions)}
                />
            );
        case SystemStandardField.SystemCertifications:
            return (
                <SystemCertificationsField
                    certifications={system?.certifications ?? []}
                    onChange={(value) =>
                        onChange((s) => {
                            s.certifications = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemCertifications].toString())?.error}
                />
            );
        case SystemStandardField.SystemDataStorageLocations:
            return (
                <SystemDataStorageLocationsField
                    dataStorageLocations={system?.dataStorageLocations ?? []}
                    onChange={(locations) =>
                        onChange((s) => {
                            s.dataStorageLocations = locations;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDataStorageLocations].toString())?.error}
                />
            );
        case SystemStandardField.SystemSupportAccessLocations:
            return (
                <SystemSupportAccessLocationsField
                    supportAccessLocations={system?.supportAccessLocations ?? []}
                    onChange={(locations) =>
                        onChange((s) => {
                            s.supportAccessLocations = locations;
                        })
                    }
                    disabled={isDisabled}
                    errorText={
                        validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemSupportAccessLocations].toString())?.error
                    }
                />
            );
        case SystemStandardField.SystemSecurityMeasures:
            return (
                <SystemSecurityMeasuresField
                    entityId={systemId}
                    securityMeasures={system?.securityMeasures ?? []}
                    onChange={(value) =>
                        onChange((s) => {
                            s.securityMeasures = value;
                        })
                    }
                    canManageMasterData={permissions.canManageMasterData}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemSecurityMeasures].toString())?.error}
                />
            );
        case SystemStandardField.SystemPolicies:
            return (
                <SystemPoliciesField
                    policies={system?.policies ?? []}
                    onChange={(value) =>
                        onChange((s) => {
                            s.policies = value;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemPolicies].toString())?.error}
                />
            );
        case SystemStandardField.SystemDataTypes:
            return (
                <SystemDataTypesField
                    dataTypes={system?.dataTypes ?? []}
                    onChange={(types) =>
                        onChange((s) => {
                            s.dataTypes = types.map((t) => Number(t));
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDataTypes].toString())?.error}
                />
            );
        case SystemStandardField.SystemTags:
            return (
                <TagsMultiSelectStandAlone
                    entityId={systemId}
                    isLoading={false}
                    entityType={TagRelationType.System}
                    disabled={isDisabled}
                    tooltipText={translateString("systemTagsTooltip")}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemTags].toString())?.error}
                    disableQuery={isPreview}
                />
            );

        case SystemStandardField.SystemAssociatedAssets:
            return (
                <SystemAssociatedAssetsField
                    systemId={systemId}
                    disableSystemRelations={system?.disableSystemRelations ?? false}
                    onDisableSystemRelationsChange={(checked) =>
                        onChange((s) => {
                            s.disableSystemRelations = checked;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemAssociatedAssets].toString())?.error}
                />
            );
        case SystemStandardField.SystemDataSubjects:
            return (
                <SystemDataSubjectsField
                    systemId={systemId}
                    system={system}
                    isLoading={isLoading}
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDataSubjects].toString())?.error}
                />
            );
        case SystemStandardField.SystemDataCategories:
            return (
                <SystemDataCategoriesField
                    systemId={systemId}
                    system={system}
                    isLoading={isLoading}
                    isPreview={isPreview}
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemDataCategories].toString())?.error}
                />
            );
        case SystemStandardField.SystemAdditionalUserAccess:
            return (
                <AdditionalUserAccessField
                    entity={system}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getSystemsUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("systemAdditionalAccessTooltip")}
                    warningText={translateString("systemAdditionalAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemAdditionalUserAccess].toString())?.error}
                />
            );
        case SystemStandardField.SystemExcludedUsers:
            return (
                <SystemExcludedUsersField
                    systemId={systemId}
                    system={system}
                    isLoading={isLoading}
                    onChange={onChange}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("systemExcludedUsersTooltip")}
                    warningText={translateString("systemExcludedUsersWarning")}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemExcludedUsers].toString())?.error}
                />
            );
        case SystemStandardField.SystemVisibleToAllUsers:
            return (
                <VisibleToAllUsersField
                    entity={system}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getSystemsUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("visibleToAllUsersTooltip")}
                    confirmAccessRemovalKey="confirmSystemAccessRemovalBody"
                    disableAccessWarning={disableAccessWarning}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemVisibleToAllUsers].toString())?.error}
                />
            );
        case SystemStandardField.SystemBusinessAreaAccess:
            return (
                <BusinessAreaAccessField
                    entity={system}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getSystemsUrl())}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("systemBusinessAreaAccessTooltip")}
                    warningText={translateString("systemBusinessAreaAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemBusinessAreaAccess].toString())?.error}
                />
            );
        case SystemStandardField.SystemGroupCompanies:
            return (
                <GroupCompaniesAccessField
                    entity={system}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={() => navigate(urlProvider.getSystemsUrl())}
                    addGroupEntityDialogHeader={translateString("addCompaniesToSystem")}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemGroupCompanies].toString())?.error}
                    disableAccessWarning={disableAccessWarning}
                    confirmAccessRemovalKey="confirmSystemAccessRemovalBody"
                />
            );
        case SystemStandardField.SystemStatus:
            return (
                <SystemStatusField
                    status={system?.status}
                    onChange={(status) =>
                        onChange((s) => {
                            s.status = status;
                        })
                    }
                    disabled={isDisabled}
                    errorText={validationErrors.find((v) => v.field === SystemStandardField[SystemStandardField.SystemStorageType].toString())?.error}
                    isLoading={isLoading}
                />
            );
        default:
            return exhaustiveGuard(field.standardFieldIdentifier);
    }
};

export const SystemCustomFieldRenderer: React.FC<SystemCustomFieldProps> = ({
    system,
    systemId,
    field,
    onChange,
    isPreview,
    isLoading = false,
    validationErrors,
    computedFieldEditing,
}) => {
    if (!field.customInputFieldType) {
        return null;
    }

    const isDisabled = isPreview || field.disabled;
    const value = (system?.customFieldValues ?? []).find((x) => x.customInputFieldId == field.customInputFieldId)?.value ?? "";
    switch (field.customInputFieldType!) {
        case InputFieldTypeEnum.TextField:
            return (
                <CustomTextField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.RichTextField:
            return (
                <CustomRichTextField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.CountryField:
            return (
                <CustomCountryField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.SingleDropDown:
            return (
                <CustomSingleSelectField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.MultiDropDown:
            return (
                <CustomMultiSelectField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.DateField:
            return (
                <CustomDateField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as Date | null}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.NumberField:
            return (
                <CustomNumberField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.AmountField:
            return (
                <CustomAmountField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as { Amount: number; CurrencyCode: string }}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.CheckboxField:
            return (
                <CustomCheckbox
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as boolean}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.LegalEntityField:
            return (
                <CustomLegalEntityField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.UserField:
            return (
                <CustomUserField
                    isLoading={isLoading}
                    entityId={systemId}
                    entityApiPath="system"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );
        case InputFieldTypeEnum.CalculatedField:
            return (
                <CustomCalculatedField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                    templateEntityType={TemplateEntityType.System}
                    computedFieldEditing={computedFieldEditing}
                    entityTemplateId={system?.entityTemplateId}
                    entity={{ customFieldValues: system?.customFieldValues ?? [] }}
                />
            );

        case InputFieldTypeEnum.DatePeriodField:
            return (
                <CustomDatePeriodField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                    templateEntityType={TemplateEntityType.System}
                    computedFieldEditing={computedFieldEditing}
                    entity={{ customFieldValues: system?.customFieldValues ?? [] }}
                />
            );

        default:
            exhaustiveGuard(field.customInputFieldType!);
    }
};

export const SystemFieldRenderer: React.FC<SystemFieldRendererProps> = (props) => {
    if (props.field.standardFieldIdentifier) {
        return (
            <SystemStandardFieldRenderer
                field={props.field}
                system={props.system}
                systemId={props.systemId!}
                onChange={props.onChange}
                validationErrors={props.validationErrors || []}
                isPreview={props.isPreview}
                isLoading={props.isLoading}
                onRefetchSystem={props.onRefetchSystem}
            />
        );
    } else {
        return (
            <SystemCustomFieldRenderer
                system={props.system}
                field={props.field}
                onChange={props.onCustomFieldChange}
                isPreview={props.isPreview}
                systemId={props.systemId!}
                isLoading={props.isLoading}
                validationErrors={props.validationErrors || []}
                computedFieldEditing={props.computedFieldEditing}
            />
        );
    }
};
